import { ReactNode, useMemo } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import { MoreHorizontal } from 'lucide-react';

import { MigrationDevice } from '../api/migrations';
import { isCanaryDevice } from '../lib/deviceOrder';
import { phaseLabelForActionId } from '../lib/devicePhase';
import { DataTable, DataTableColumn, DataTableSelection } from './DataTable';
import MappingStatusChip, { MappingChipStatus } from './identity/MappingStatusChip';
import ReadinessBadge from './ReadinessBadge';
import RelativeTime from './RelativeTime';
import StatusBadge from './StatusBadge';
import { IDENTIFIER, STATE, TEXT } from '../theme/tokens';

const EMPTY = '—';

// Optional identity-mapping surface (FD-098). When provided, the grid adds a
// "Mapping" status-chip column. The chip status is derived by the PARENT (from
// the FD-103 identity-suggestions rows) — this component stays presentational.
// `null` from statusForDevice renders a placeholder (suggestion row not loaded
// yet, e.g. paging in flight).
export interface DeviceGridMappingColumn {
  statusForDevice: (device: MigrationDevice) => MappingChipStatus | null;
  // Chip click deep-links into the identity-mappings section row.
  onChipClick?: (device: MigrationDevice) => void;
}

/**
 * FD-503 — the per-row `···` menu's four operations.
 *
 * Each one opens the SAME dialog the bulk bar opens, scoped to one device. They
 * are not a second implementation: `MoveDevicesDialog`, `ConfirmRemoveDevicesModal`
 * and `ReadinessOverrideDialog` each perform an irreversible or audited act, and
 * two implementations of one of those is how the two come to disagree about what
 * they did.
 */
export interface DeviceRowActions {
  onOpen: (device: MigrationDevice) => void;
  onMove: (device: MigrationDevice) => void;
  onRemove: (device: MigrationDevice) => void;
  onOverrideReadiness: (device: MigrationDevice) => void;
}

export interface DeviceGridProps {
  devices: MigrationDevice[];
  onDeviceClick?: (device: MigrationDevice) => void;
  isLoading?: boolean;
  error?: ReactNode;
  emptyState?: ReactNode;
  // Optional multi-select model. When provided, DataTable renders a checkbox
  // column as the leftmost column. No device-specific selection logic lives
  // here — parent supplies keyForSelection (typically `d => d.serialNumber`).
  selection?: DataTableSelection<MigrationDevice>;
  // Optional mapping-chip column (T2T/D2C migrations only — parent gates).
  mappingColumn?: DeviceGridMappingColumn;
  // FD-206 — when provided, the grid adds a "Readiness" column. Passed as an object
  // rather than a bare boolean so the enforcement mode travels with it: a blocking
  // verdict under `observe` is not withholding anything, and the badge title has to
  // say so. Optional because the migrations list has no use for it.
  readinessColumn?: { enforcement: 'observe' | 'enforce' };
  /**
   * FD-503 — the wave's canary serial, from `Migrations.canarySerial`.
   *
   * Marks the row. ORDERING IS THE CALLER'S JOB (`pinCanaryFirst`), deliberately:
   * this component renders the rows it is given in the order it is given them,
   * and a grid that silently re-sorted its input would be a second, invisible
   * source of truth about what the table is showing.
   */
  canarySerial?: string | null;
  /**
   * FD-503 — the per-row `···` menu the mockup draws.
   *
   * EVERY ITEM IS A REAL OPERATION (FD-ADR-043 D4) and each one is the dialog
   * the bulk bar already opens, scoped to one device — never a second
   * implementation of an irreversible act.
   */
  rowActions?: DeviceRowActions;
  // FD-498 — when true, the grid adds a "Step" column reading the device's own
  // `lastActionId` (FD-496 stamps it, FD-497 projects it) through
  // `devicePhase.ts`'s stage table, so the grid and the campaign's phase
  // distribution name a stage identically.
  //
  // NOT DERIVED FROM `lastEventType`, which reads the literal `"progress"` for
  // every in-flight device in the fleet — that is the whole reason FD-496 and
  // FD-497 exist. And NOT from the event trail: one partition query per device
  // is exactly the fan-out those two tasks were built to avoid.
  //
  // GATED BY THE PARENT on the field actually arriving, like `checkInColumn`'s
  // siblings were:
  // a Manager deployed before FD-497 omits `lastActionId` entirely, and a column
  // of em-dashes across every row is worse than no column.
  stepColumn?: boolean;
  // FD-494 — when true, the grid adds a "Check-in" column beside Status.
  //
  // It is a SEPARATE QUESTION from Status, which is why it is a separate column
  // rather than a richer `StatusBadge`: Status is the FD-190 lifecycle state and
  // check-in is whether the agent has ever spoken to us. Derived from `jobId` and
  // `readinessEvaluatedUtc`, both already on every row, so it costs no request.
  //
  // Optional, and unconditional where it is switched on: unlike `stepColumn`
  // there is nothing to gate against, because both inputs have been contractually
  // present since FD-041/FD-205 and `null` is a real answer rather than an absent
  // field.
  checkInColumn?: boolean;
}

export default function DeviceGrid({
  devices,
  onDeviceClick,
  isLoading,
  error,
  emptyState,
  selection,
  mappingColumn,
  readinessColumn,
  stepColumn,
  checkInColumn,
  canarySerial,
  rowActions,
}: DeviceGridProps) {
  const columns = useMemo<DataTableColumn<MigrationDevice>[]>(() => {
    // FD-503 — EIGHT COLUMNS AND A ROW MENU, down from thirteen.
    //
    // The four that left — Job ID, Hardware hash, Last event, and the Identity
    // badge — did not leave because they are worthless; they left because a
    // table an operator scans at a glance was scrolling sideways at every
    // width, and each of them answers a question that is better answered
    // elsewhere:
    //
    //   Job ID and Last event are on the DEVICE DETAIL page, which a row click
    //   opens (FD-290). Truncated to eight characters in a cell, a jobId is an
    //   identifier nobody can read and nobody can copy.
    //
    //   Hardware hash was a 12-character prefix of a ~4 KB Autopilot blob. Its
    //   PRESENCE is the operationally meaningful fact — `ARR-APREGISTER` skips a
    //   device with `PRE_APHASH_MISSING` — so FD-503 puts that on the detail
    //   page as a present/not-reported fact rather than deleting the signal with
    //   the column.
    //
    //   The Identity badge (FD-357 `identityStatus`) overlapped the Mapping chip
    //   on the question an operator actually asks. Mapping is the richer of the
    //   two — it distinguishes "no manifest" from "unmapped" from "needs
    //   attention" — and the identity DROPDOWN FILTER above the grid still reads
    //   `identityStatus` directly, so the server's own verdict is still what
    //   filters the table.
    const cols: DataTableColumn<MigrationDevice>[] = [
      {
        key: 'serialNumber',
        header: 'Serial number',
        numeric: true,
        render: (d) => (
          <div className="flex items-center gap-2">
            <span className="font-medium" style={{ color: TEXT.primary }}>
              {d.serialNumber}
            </span>
            {isCanaryDevice(d.serialNumber, canarySerial) && <CanaryChip />}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (d) => <StatusBadge status={d.status} />,
      },
      ...(stepColumn
        ? [
            {
              key: 'step',
              header: 'Step',
              render: (d: MigrationDevice) => <StepCell device={d} />,
            },
          ]
        : []),
    ];

    if (readinessColumn) {
      cols.push({
        key: 'readiness',
        header: 'Readiness',
        // FD-223's per-row PROVENANCE line ("local policy" / a ruleset version)
        // was removed here by owner decision (2026-09-27): at this density it is
        // a per-row restatement of a per-DEPLOYMENT fact, and it read as noise
        // beside the verdict it annotated.
        //
        // The signal survives in BOTH places FD-223 put it, which is why this
        // line could go: `RulesetProvenance` on Overview's readiness panel says
        // which policy produced this campaign's verdicts once, and the device
        // detail page's "Policy" fact still answers it per device. If either is
        // removed, floor-vs-ruleset becomes unanswerable — the point of
        // deriving `readinessSource` server-side (FD-221 / FD-ADR-028 R10) was
        // that readers should never re-infer it.
        render: (d) => (
          <ReadinessBadge
            status={d.readinessStatus}
            enforcement={readinessColumn.enforcement}
            overridden={Boolean(d.readinessOverride)}
          />
        ),
      });
    }

    cols.push({
      // FD-102 manifest user facts: friendly name → SAM → UPN-ish userName →
      // truncated SID → em-dash (pre-manifest).
      key: 'signedInUser',
      header: 'Signed-in user',
      render: (d) => <SignedInUserCell device={d} />,
    });

    // FD-246 / OQ-011 — the Autopilot tag resolution. Rendered like the readiness
    // column: the value with its ORIGIN beneath, because under source-wins "HR-LAPTOP"
    // alone does not say whether the device brought that tag or the campaign supplied
    // it — and those are the two populations R-FD-032 has to reconcile.
    //
    // `unknown` is styled as a fault and `none`/null are not: unknown means the Graph
    // read FAILED and the value is a fallback to the device-reported tag, which
    // FA-126 established may be silently wrong. Collapsing it into "no tag" is exactly
    // the mistake the server-side stamp exists to prevent.
    cols.push({
      key: 'autopilotTag',
      header: 'Autopilot tag',
      render: (d) => {
        // Absent (pre-FD-236 row, or the broker has not run) is NOT 'none'.
        if (!d.autopilotTagSource) return <Placeholder />;
        const origin =
          d.autopilotTagSource === 'source'
            ? 'from source'
            : d.autopilotTagSource === 'campaign'
              ? 'campaign default'
              : d.autopilotTagSource === 'unknown'
                ? 'read failed'
                : 'no tag';
        return (
          <div className="flex flex-col gap-0.5">
            {d.autopilotResolvedGroupTag ? (
              <span className="text-xs" style={{ ...IDENTIFIER, color: TEXT.primary }}>
                {d.autopilotResolvedGroupTag}
              </span>
            ) : (
              <span className="text-xs" style={{ color: TEXT.secondary }}>
                —
              </span>
            )}
            <span
              className="text-[10px] leading-tight"
              style={
                d.autopilotTagSource === 'unknown'
                  ? { color: STATE.critical.text, fontWeight: 500 }
                  : { color: TEXT.secondary }
              }
            >
              {origin}
            </span>
          </div>
        );
      },
    });

    if (mappingColumn) {
      cols.push({
        key: 'mapping',
        header: 'Mapping',
        render: (d) => {
          const status = mappingColumn.statusForDevice(d);
          if (status === null) return <Placeholder />;
          const chip = <MappingStatusChip status={status} />;
          if (!mappingColumn.onChipClick) return chip;
          return (
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent p-0"
              aria-label={`Show mapping for ${d.serialNumber}`}
              onClick={(e) => {
                // Chip navigation is orthogonal to the row click (device detail).
                e.stopPropagation();
                mappingColumn.onChipClick!(d);
              }}
            >
              {chip}
            </button>
          );
        },
      });
    }

    if (checkInColumn) {
      cols.push({
        key: 'lastCheckIn',
        header: 'Last check-in',
        render: (d: MigrationDevice) => <LastCheckInCell device={d} />,
      });
    }

    if (rowActions) {
      cols.push({
        key: 'rowActions',
        header: '',
        align: 'right',
        width: '48px',
        render: (d: MigrationDevice) => <RowActionsMenu device={d} actions={rowActions} />,
      });
    }

    return cols;
  }, [mappingColumn, readinessColumn, stepColumn, checkInColumn, canarySerial, rowActions]);

  return (
    <DataTable
      columns={columns}
      data={devices}
      keyForRow={(d) => d.serialNumber}
      onRowClick={onDeviceClick}
      isLoading={isLoading}
      error={error}
      emptyState={emptyState}
      selection={selection}
    />
  );
}

// FD-102 fallback chain per the FD-098 scope: primaryUserDisplayName →
// primaryUserSamName → primaryUserName → truncated mono SID → em-dash.
function SignedInUserCell({ device }: { device: MigrationDevice }) {
  const friendly =
    device.primaryUserDisplayName ?? device.primaryUserSamName ?? device.primaryUserName;
  if (friendly) {
    return <span className="text-zinc-900">{friendly}</span>;
  }
  if (device.primaryUserSid) {
    return (
      <span className="font-mono text-xs text-zinc-700" title={device.primaryUserSid}>
        {truncate(device.primaryUserSid, 18)}
      </span>
    );
  }
  return <Placeholder />;
}

/**
 * The Step cell (FD-498) — the stage, with the raw action id beneath it.
 *
 * THREE STATES, AND NONE OF THEM GUESSES:
 *
 *   * no `lastActionId` — the device has never reported progress (most devices,
 *     most of the time) or this Manager predates FD-497. An honest em-dash, not
 *     an inferred phase.
 *   * an id whose stage prefix the console has not been taught — the id renders
 *     VERBATIM with no stage claim above it. `devicePhase.ts` ignores an
 *     unrecognised prefix rather than defaulting it, and showing "Arrival" over
 *     an action the console cannot place would be inventing the one fact this
 *     column exists to report.
 *   * a recognised id — the stage label, with the action id and the device's own
 *     word for how it went beneath, the value-over-origin shape the readiness and
 *     Autopilot-tag columns already use.
 *
 * `lastActionStatus` is the DEVICE's word (`started` | `succeeded` | `failed` |
 * `skipped`), not the envelope's mapped status, and `failed` is the one value
 * painted — an operator scanning this column is looking for exactly that.
 */
function StepCell({ device }: { device: MigrationDevice }) {
  const actionId = typeof device.lastActionId === 'string' ? device.lastActionId.trim() : '';
  if (!actionId) return <Placeholder />;

  const label = phaseLabelForActionId(actionId);
  const status = (device.lastActionStatus ?? '').trim().toLowerCase();
  const failed = status === 'failed';

  return (
    <div className="flex flex-col gap-0.5">
      {label ? (
        <span className="text-xs text-zinc-900">{label}</span>
      ) : (
        <span className="text-xs text-zinc-900" style={IDENTIFIER}>
          {actionId}
        </span>
      )}
      <span
        className={
          failed
            ? 'text-[10px] leading-tight font-medium text-red-600'
            : 'text-[10px] leading-tight text-zinc-500'
        }
        style={IDENTIFIER}
      >
        {label ? actionId : 'unrecognised stage'}
        {status ? ` · ${status}` : ''}
      </span>
    </div>
  );
}

/**
 * The canary marker (FD-503, FD-ADR-035 decision 4).
 *
 * The pin alone would look like a sort nobody asked for; this is what says why
 * the row is first. Amber rather than red, per the console's status vocabulary:
 * a canary is not a fault, it is the row a human should be watching — and the
 * whole wave is held at `409 CANARY_HOLD` until it completes.
 */
function CanaryChip() {
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium"
      style={{ backgroundColor: STATE.warning.soft, color: STATE.warning.text }}
      title="The wave's canary. Every other device holds until this one completes."
    >
      Canary
    </span>
  );
}

/**
 * Last check-in — the time, and only the time.
 *
 * FD-503 rendered the derived check-in STATE beneath it; the owner removed that
 * second line on 2026-09-27. What the column now carries is narrower than its
 * header suggests, and the narrowing is recorded rather than papered over:
 *
 * THE TIMESTAMP IS LAST CONTACT, NOT NECESSARILY A DEVICE REPORT.
 * `lastEventTimestamp` is stamped by `api/WriteTable` for every event on the
 * device's partition, which includes the Manager's own broker events — so a
 * device that has never checked in can still show a time once the broker acts
 * on it. The per-device check-in state (`deriveCheckIn`, from `jobId` and
 * `readinessEvaluatedUtc`) is the fact a server-side event cannot move, and it
 * is still what the Status and Readiness columns are downstream of; it is just
 * no longer spelled out here.
 */
function LastCheckInCell({ device }: { device: MigrationDevice }) {
  if (!device.lastEventTimestamp) return <Placeholder />;
  return <RelativeTime iso={device.lastEventTimestamp} />;
}

/**
 * The row `···` (FD-503).
 *
 * FOUR REAL OPERATIONS, and nothing else (FD-ADR-043 D4). The mockup draws this
 * menu without saying what is in it; every item here resolves to a dialog or a
 * route that already exists, and the click is stopped from bubbling so the menu
 * never also opens the device the row points at.
 */
function RowActionsMenu({
  device,
  actions,
}: {
  device: MigrationDevice;
  actions: DeviceRowActions;
}) {
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            size="small"
            aria-label={`Actions for ${device.serialNumber}`}
            icon={<MoreHorizontal size={16} strokeWidth={1.5} />}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem onClick={() => actions.onOpen(device)}>Open device</MenuItem>
            <MenuItem onClick={() => actions.onOverrideReadiness(device)}>
              Override readiness…
            </MenuItem>
            <MenuItem onClick={() => actions.onMove(device)}>Move to migration…</MenuItem>
            <MenuItem onClick={() => actions.onRemove(device)}>Remove from campaign…</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </span>
  );
}

function Placeholder() {
  return <span className="text-zinc-400">{EMPTY}</span>;
}

function truncate(value: string, prefixLength: number) {
  if (value.length <= prefixLength + 1) return value;
  return `${value.slice(0, prefixLength)}…`;
}
